import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Info, Upload, Save, Loader2, ImageIcon, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/utils/format";

const REGS = [
  { key: "gstin", label: "GSTIN", note: "GSTIN is a tax-invoice identifier and is not normally shown on a payslip. Enable only if you specifically want it." },
  { key: "ptrc", label: "PTRC (Professional Tax reg.)" },
  { key: "shops", label: "Shops & Establishments reg. no." },
  { key: "epf", label: "EPF establishment code" },
  { key: "esi", label: "ESI code" },
  { key: "tan", label: "TAN" },
];

export default function CompanySettings() {
  const [s, setS] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [demoRemoved, setDemoRemoved] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());
  const fileRef = useRef();

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/settings/pt-slabs")])
      .then(([a, b]) => { setS(a.data); setSlabs(b.data); });
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setReg = (k, field, v) => setS((p) => ({ ...p, [k]: { ...p[k], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      delete payload.has_logo;
      await api.put("/settings", payload);
      await api.put("/settings/pt-slabs", { slabs: slabs.map((x) => ({ lower: Number(x.lower), upper: x.upper === "" || x.upper == null ? null : Number(x.upper), amount: Number(x.amount) })) });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const purgeDemo = async () => {
    try {
      const { data } = await api.post("/settings/purge-demo");
      setDemoRemoved(data.removed);
      toast.success(data.removed
        ? `Removed ${data.removed} sample employee${data.removed > 1 ? "s" : ""} and their demo payslips`
        : "No sample data found — nothing to remove");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((p) => ({ ...p, has_logo: true }));
      setLogoBust(Date.now());
      toast.success("Logo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!s) return <Loading />;
  const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/settings/logo?t=${logoBust}`;

  return (
    <div className="pb-4">
      <PageHeader title="Company Settings" description="Everything here flows onto the payslip. Nothing is hardcoded." testid="settings-page">
        <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="save-settings-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </Button>
      </PageHeader>

      <div className="space-y-6 max-w-4xl">
        {/* Identity */}
        <Section title="Identity">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Trade name (required)"><Input value={s.trade_name || ""} onChange={(e) => set("trade_name", e.target.value)} data-testid="settings-trade-name" /></Field>
            <Field label="Proprietor / owner name">
              <Input value={s.proprietor_name || ""} onChange={(e) => set("proprietor_name", e.target.value)} />
              <div className="flex items-center justify-between mt-2 border border-border rounded-md px-3 py-2">
                <span className="text-xs text-muted-foreground">Show on payslip</span>
                <Switch checked={!!s.show_proprietor} onCheckedChange={(v) => set("show_proprietor", v)} data-testid="settings-show-proprietor" />
              </div>
            </Field>
            <Field label="Business PAN"><Input value={s.business_pan || ""} onChange={(e) => set("business_pan", e.target.value.toUpperCase())} /></Field>
            <Field label="Company logo">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-md border border-border bg-secondary/40 flex items-center justify-center overflow-hidden shrink-0">
                  {s.has_logo ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" data-testid="settings-logo-preview" /> : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} data-testid="settings-logo-input" />
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="upload-logo-button"><Upload className="w-4 h-4" /> Upload PNG/JPG</Button>
              </div>
            </Field>
          </div>
        </Section>

        {/* Address */}
        <Section title="Registered address">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Address line 1"><Input value={s.address1 || ""} onChange={(e) => set("address1", e.target.value)} /></Field>
            <Field label="Address line 2"><Input value={s.address2 || ""} onChange={(e) => set("address2", e.target.value)} /></Field>
            <Field label="City"><Input value={s.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="State"><Input value={s.state || ""} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="PIN code"><Input value={s.pin || ""} onChange={(e) => set("pin", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Registrations */}
        <Section title="Statutory registrations" subtitle="Toggle which numbers appear on the payslip.">
          <div className="space-y-3">
            {REGS.map((r) => (
              <div key={r.key} className="border border-border rounded-md p-4" data-testid={`reg-${r.key}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="sm:w-64 shrink-0">
                    <div className="text-sm font-medium">{r.label}</div>
                  </div>
                  <Input value={s[r.key]?.value || ""} onChange={(e) => setReg(r.key, "value", e.target.value)} placeholder="Number" className="flex-1" data-testid={`reg-${r.key}-input`} />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Show on payslip</span>
                    <Switch checked={!!s[r.key]?.show} onCheckedChange={(v) => setReg(r.key, "show", v)} data-testid={`reg-${r.key}-switch`} />
                  </div>
                </div>
                {r.note && (
                  <div className="flex items-start gap-2 mt-3 text-xs text-muted-foreground bg-secondary/50 rounded px-2.5 py-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {r.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Payroll config */}
        <Section title="Payroll configuration">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Working week">
              <Select value={s.working_week} onValueChange={(v) => set("working_week", v)}>
                <SelectTrigger data-testid="settings-working-week"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mon_fri">Monday – Friday</SelectItem>
                  <SelectItem value="mon_sat">Monday – Saturday</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Salary basis (transfer amount)">
              <Select value={s.salary_basis} onValueChange={(v) => set("salary_basis", v)}>
                <SelectTrigger data-testid="settings-salary-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="net">Transfer amount is NET</SelectItem>
                  <SelectItem value="gross">Transfer amount is GROSS</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="HRA % of Basic"><Input type="number" value={s.hra_pct} onChange={(e) => set("hra_pct", Number(e.target.value))} data-testid="settings-hra-pct" /></Field>
            <Field label="Basic % of Gross (min 50)"><Input type="number" min={50} value={s.basic_pct} onChange={(e) => set("basic_pct", Number(e.target.value))} data-testid="settings-basic-pct" /></Field>
          </div>
          <Field label="Payslip footer text" className="mt-5">
            <Textarea value={s.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} rows={2} data-testid="settings-footer" />
          </Field>
        </Section>

        {/* PT slabs */}
        <Section title="Professional Tax slabs" subtitle="Monthly gross thresholds (₹). Leave the upper limit of the last slab empty for 'and above'.">
          <div className="space-y-2" data-testid="pt-slabs-editor">
            <div className="grid grid-cols-4 gap-3 label-caps px-1">
              <span>From (₹)</span><span>To (₹)</span><span>PT (₹)</span><span></span>
            </div>
            {slabs.map((sl, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 items-center">
                <Input type="number" value={sl.lower} onChange={(e) => updateSlab(setSlabs, i, "lower", e.target.value)} data-testid={`pt-lower-${i}`} />
                <Input type="number" value={sl.upper ?? ""} placeholder="∞" onChange={(e) => updateSlab(setSlabs, i, "upper", e.target.value)} data-testid={`pt-upper-${i}`} />
                <Input type="number" value={sl.amount} onChange={(e) => updateSlab(setSlabs, i, "amount", e.target.value)} data-testid={`pt-amount-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => setSlabs(slabs.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setSlabs([...slabs, { lower: 0, upper: null, amount: 0 }])} data-testid="add-pt-slab">+ Add slab</Button>
          </div>
        </Section>

        {/* Danger zone */}
        <div className="border border-destructive/30 bg-destructive/[0.03] rounded-md p-6" data-testid="danger-zone">
          <h2 className="font-display font-semibold text-lg text-destructive flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Danger zone
          </h2>
          <p className="text-sm text-muted-foreground mt-1 mb-5 max-w-2xl">
            Remove the pre-loaded <b>sample employees</b> (Aarav, Priya, Rohan, Sneha) and their demo payslips that were added for you to explore the app. Employees you created yourself are <b>not</b> affected. Once removed, your next employee can take ID <b>PC-001</b>.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="purge-demo-button">
                <Trash2 className="w-4 h-4" /> Remove sample data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove sample data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes the 4 pre-loaded demo employees and their sample payslips. This cannot be undone. Your own employees, company settings, holidays and designations are left untouched.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={purgeDemo} data-testid="confirm-purge-demo">Remove sample data</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {demoRemoved !== null && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="purge-result">
              {demoRemoved ? `Removed ${demoRemoved} sample employees. You're all set to add your own.` : "No sample data was present."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function updateSlab(setSlabs, i, field, value) {
  setSlabs((prev) => prev.map((sl, j) => j === i ? { ...sl, [field]: value } : sl));
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-card border border-border rounded-md p-6">
      <h2 className="font-display font-semibold text-lg">{title}</h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5 mb-5">{subtitle}</p>}
      <div className={subtitle ? "" : "mt-5"}>{children}</div>
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="label-caps">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
